import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  InputAdornment,
  Checkbox,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  DialogContentText,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { saveAs } from "file-saver";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import axiosInstance from "../../Utils/axiosInstance";
import ClearIcon from "@mui/icons-material/Clear";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CalculateIcon from "@mui/icons-material/Calculate";
import Accordion from "react-bootstrap/Accordion";
import { MaterialReactTable, MRT_Row, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton } from "material-react-table";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";

interface FormData {
  [key: string]: string | number;
}

const EXCHANGE_CODES = {
  I: "ICE Energy",
  L: "Financials",
  O: "Equity Options",
  X: "Commodities",
  F: "FX",
  G: "ICE Clear Singapore",
  N: "ICE Clear U.S.",
  T: "ICE Clear Netherlands",
};

const CONTRACT_TYPES = {
  F: "Futures",
  C: "Call Options",
  P: "Put Options",
  M: "Monthly Contracts",
  D: "Daily Contracts",
  N: "NDF (OTC FX Contracts)",
};

const REGIMES = ["", "DCO", "RCH"];
const CUSTOMER_TYPES = ["", "H", "S", "M"];
const CSV_COLUMNS = [
  "PositionFlag",
  "Account",
  "ExchangeCode",
  "ContractCode",
  "ContractType",
  "ExpiryYYYYMMDD",
  "Strike",
  "NetPosition",
  "Regime",
  "CustomerType",
];

const makeEmptyRow = () => ({
  PositionFlag: "P",
  Account: "",
  ExchangeCode: "I",
  ContractCode: "",
  ContractType: "F",
  ExpiryYYYYMMDD: "",
  Strike: "",
  NetPosition: "",
  Regime: "",
  CustomerType: "",
});
type SelectOption = {
  value: string;
  label: string;
};

type PositionRow = {
  PositionFlag: string;
  Account: string;
  ExchangeCode: string;
  ContractCode: string;
  ContractType: string;
  ExpiryYYYYMMDD: string;
  Strike: string;
  NetPosition: string;
  Regime?: string;
  CustomerType?: string;
};

const FIELD_LABELS: Record<string, string> = {
  PositionFlag: "Position/Trade Flag",
  Account: "Account Name/Identifier",
  ExchangeCode: "Exchange Code",
  ContractCode: "Exchange Contract Code",
  ContractType: "Contract Type",
  ExpiryYYYYMMDD: "Expiry (YYYYMMDD)",
  Strike: "Strike Price",
  NetPosition: "Net Position",
  Regime: "Regime",
  CustomerType: "Customer Type",
};

type Book = {
  id: string | number;
  name: string;
  // Add other fields if the API returns more data
};
const InitialMargin: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormData>(makeEmptyRow());
  const [editIndex, setEditIndex] = useState<number | number[] | null>(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    exchange: "",
    book: "",
    strategy: "",
    clearer: "",
    companyType: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [bulkDeleteRows, setBulkDeleteRows] = useState<any[]>([]);

  const [bookOptions, setBookOptions] = useState<SelectOption[]>([]);
  const [custBookOptions, setCustBookOptions] = useState<SelectOption[]>([]);
  const [clearerOptions, setClearerOptions] = useState<SelectOption[]>([]);
  const [strategyOptions, setStrategyOptions] = useState<SelectOption[]>([]);
  const [allBookOptions, setAllBookOptions] = useState<SelectOption[]>([]);

  const [isLoadingBooks, setIsLoadingBooks] = useState(false);
  const [isLoadingCustBooks, setIsLoadingCustBooks] = useState(false);
  const [isLoadingClearers, setIsLoadingClearers] = useState(false);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [rowSelection, setRowSelection] = useState({});

  const [isCalculating, setIsCalculating] = useState(false);
  const [imResult, setImResult] = useState<any | null>(null);
  const [showImTable, setShowImTable] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [showActionsHeader, setShowActionsHeader] = useState(false);

  const fetchDataBook = async () => {
    // ✅ no export
    try {
      setIsLoadingBooks(true);
      const response = await axiosInstance.get("/paper-app/books/");
      const apiData = response.data;

      let books: Book[] = [];
      if (Array.isArray(apiData)) books = apiData;
      else if (Array.isArray(apiData.data)) books = apiData.data;
      else if (Array.isArray(apiData.results)) books = apiData.results;

      const options: SelectOption[] = [
        { value: "", label: "" },
        ...books.map((book) => ({
          value: String(book.name),
          label: book.name,
        })),
      ];

      setBookOptions(options);
      setAllBookOptions(options);
    } catch (error) {
      console.error("Error fetching books:", error);
    } finally {
      setIsLoadingBooks(false);
    }
  };
  const fetchDataCustBooks = async () => {
    try {
      setIsLoadingCustBooks(true);
      const response = await axiosInstance.get(
        "/paper-app/counterparty-companies/"
      );
      const apiData = response.data;

      let companies: any[] = [];
      if (Array.isArray(apiData)) {
        companies = apiData;
      } else if (apiData?.results && Array.isArray(apiData.results)) {
        companies = apiData.results;
      }

      const options: SelectOption[] = [
        { value: "", label: "" }, // empty option
        ...companies.map((c) => ({
          value: c.customer_company_name, // ✅ only customer_company_name
          label: c.customer_company_name,
        })),
      ];

      setCustBookOptions(options);
    } catch (error) {
      console.error("Error fetching customer companies:", error);
    } finally {
      setIsLoadingCustBooks(false);
    }
  };

  const fetchDataClearers = async () => {
    try {
      setIsLoadingClearers(true);
      const response = await axiosInstance.get("/paper-app/clearers/");
      let apiData = response.data;

      let clearers: any[] = [];
      if (Array.isArray(apiData)) {
        clearers = apiData;
      } else if (apiData?.results && Array.isArray(apiData.results)) {
        clearers = apiData.results;
      }

      const options: SelectOption[] = [
        { value: "", label: "" },
        ...clearers.map((clr) => ({
          value: String(clr.name || clr.id),
          label: clr.name || clr.id,
        })),
      ];
      setClearerOptions(options);
    } catch (error) {
      console.error("Error fetching clearers:", error);
    } finally {
      setIsLoadingClearers(false);
    }
  };

  const fetchDataStrategies = async () => {
    try {
      setIsLoadingStrategies(true);
      const response = await axiosInstance.get("/paper-app/strategies/");
      let apiData = response.data;

      let strategies: any[] = [];
      if (Array.isArray(apiData)) {
        strategies = apiData;
      } else if (apiData?.results && Array.isArray(apiData.results)) {
        strategies = apiData.results;
      }

      const options: SelectOption[] = [
        { value: "", label: "" },
        ...strategies.map((st) => ({
          value: String(st.name),
          label: st.name || st.id,
        })),
      ];
      setStrategyOptions(options);
    } catch (error) {
      console.error("Error fetching strategies:", error);
    } finally {
      setIsLoadingStrategies(false);
    }
  };

  const handleCalculateIM = async () => {
    try {
      // Get selected rows from the table
      const selectedRows = Object.keys(rowSelection).map(index => rows[parseInt(index)]);

      if (selectedRows.length === 0) {
        toast.error("Please select rows to calculate IM");
        return;
      }

      setIsCalculating(true);
      setImResult(null);
      setShowImTable(false);

      // Use selectedRows instead of all rows
      const csvData = selectedRows.map((r) => Object.values(r).join(",")).join("\n");
      const csvBlob = new Blob([csvData], { type: "text/csv" });
      const formData = new FormData();
      formData.append("file", csvBlob, "Positions.csv");

      const response = await axiosInstance.post(
        "/paper-app/run-im/",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setImResult(response.data);
      toast.success(`IM calculation completed for ${selectedRows.length} rows!`);
      setRowSelection({});
    } catch (error: any) {
      console.error("Error calculating IM:", error);
      toast.error(error.response?.data?.message || "Failed to calculate IM");
    } finally {
      setIsCalculating(false);
    }
  };
  const handleValidateAll = () => {
    const allErrors: Record<number, string[]> = {};

    rows.forEach((row, index) => {
      const errors = validateRow(row);
      if (errors.length > 0) {
        allErrors[index] = errors;
      }
    });

    if (Object.keys(allErrors).length === 0) {
      toast.success("All rows are valid");
    } else {
      Object.entries(allErrors).forEach(([idx, errs]) => {
        toast.error(`Row ${idx}: ${errs.join("; ")}`);
      });
    }
  };

  useEffect(() => {
    fetchDataBook();
    fetchDataClearers();
    fetchDataStrategies();
    fetchDataCustBooks();
  }, []);

  useEffect(() => {
    if (filters.companyType === "Counterparty") {
      setBookOptions(custBookOptions);
    } else {
      setBookOptions(allBookOptions);
    }

    // Always reset the selected book when switching company type
    setFilters((prev) => ({ ...prev, book: "" }));
  }, [filters.companyType, custBookOptions, allBookOptions]);

  const filtersConfig = [
    {
      label: "Exchange",
      key: "exchange",
      options: ["ICE", "EEX", "NYMEX"], // static
    },
    {
      label: "Company Type",
      key: "companyType",
      options: ["Organization", "Counterparty"], // static
    },
    {
      label: "Book",
      key: "book",
      options: bookOptions.map((b) => b.label), // dynamic
    },
    {
      label: "Strategy",
      key: "strategy",
      options: strategyOptions.map((s) => s.label), // dynamic
    },
    {
      label: "Clearer",
      key: "clearer",
      options: clearerOptions.map((c) => c.label), // dynamic
    },
  ];

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasFormChanges(true);
  };

  const validateRow = (row: any): string[] => {
    const errors: string[] = [];

    // A: PositionFlag must be 'P'
    if (row.PositionFlag?.trim().toUpperCase() !== "P") {
      errors.push("PositionFlag must be 'P'.");
    }

    // B: Account non-empty
    if (!row.Account?.trim()) {
      errors.push("Account name is required.");
    }

    // C: Exchange code valid
    const validExchanges = ["I", "L", "O", "X", "F", "G", "N", "T"];
    if (!validExchanges.includes(row.ExchangeCode?.trim().toUpperCase())) {
      errors.push(`ExchangeCode '${row.ExchangeCode}' is invalid.`);
    }

    // D: ContractCode non-empty
    if (!row.ContractCode?.trim()) {
      errors.push("ContractCode is required.");
    }

    // E: Contract type valid
    const validTypes = ["F", "C", "P", "M", "D", "N"];
    if (!validTypes.includes(row.ContractType?.trim().toUpperCase())) {
      errors.push(`ContractType '${row.ContractType}' is invalid.`);
    }

    // F: Expiry format YYYYMMDD (or YYYYMM00)
    const expiry = String(row.ExpiryYYYYMMDD || "").trim();
    if (!expiry) {
      errors.push("ExpiryYYYYMMDD is required (YYYYMMDD or YYYYMM00).");
    } else if (!/^\d{8}$/.test(expiry)) {
      errors.push("Expiry must be 8 digits (YYYYMMDD).");
    } else {
      const year = Number(expiry.slice(0, 4));
      const month = Number(expiry.slice(4, 6));
      const day = Number(expiry.slice(6, 8));
      if (month < 1 || month > 12) {
        errors.push("Expiry month must be between 01 and 12.");
      } else if (day !== 0) {
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime()) || date.getMonth() + 1 !== month) {
          errors.push("Expiry day is invalid for given month/year.");
        }
      }
    }

    // G: Strike validation
    const ct = String(row.ContractType || "")
      .trim()
      .toUpperCase();
    const strike = String(row.Strike || "").trim();

    if (["C", "P"].includes(ct)) {
      if (!strike) {
        errors.push("Strike is required for options.");
      } else if (isNaN(Number(strike))) {
        errors.push("Strike must be numeric for options.");
      }
    } else if (strike && isNaN(Number(strike))) {
      errors.push("Strike must be numeric or blank for non-options.");
    }

    // H: NetPosition numeric
    const np = String(row.NetPosition ?? "").trim();
    if (!np) {
      errors.push("NetPosition is required.");
    } else if (isNaN(Number(np))) {
      errors.push("NetPosition must be numeric (positive or negative).");
    }

    // I: Regime optional but must be valid
    const validRegimes = ["", "DCO", "RCH"];
    if (row.Regime && !validRegimes.includes(row.Regime.trim().toUpperCase())) {
      errors.push("Regime must be DCO or RCH if specified.");
    }

    // J: CustomerType optional but must be valid
    const validCust = ["", "H", "S", "M"];
    if (
      row.CustomerType &&
      !validCust.includes(row.CustomerType.trim().toUpperCase())
    ) {
      errors.push("CustomerType must be H, S or M if specified.");
    }

    return errors;
  };

  const handleFilterApply = async () => {
    setImResult([]);
    try {
      // Build query params only from selected filters
      const params: Record<string, string> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value; // Only include non-empty filters
      });

      console.log("Applying filters:", params);

      // Call API with query params
      const response = await axiosInstance.get(
        "/paper-app/intial-margin-position/",
        { params }
      );

      console.log("API Response:", response.data);

      // ✅ Check if API returned message instead of data
      if (
        response.data?.message ===
        "No trades found for either futures or swaps." ||
        (typeof response.data === "string" &&
          response.data.includes("No trades found for either futures or swaps"))
      ) {
        toast.warn("No trades found for either futures or swaps.", {
          position: "top-right",
        });
        return; // Stop further execution
      }

      // Map API data to your row structure
      const apiRows = response.data.map((item: any) => ({
        PositionFlag: item["Position Flag"] || "P",
        Account: item["Account"] || "",
        ExchangeCode: item["Exchange Code"] || "",
        ContractCode: item["Contract Code"] || "",
        ContractType: item["Contract Type"] || "F",
        ExpiryYYYYMMDD: item["Expiry"] || "",
        Strike: item["Strike Price"] || 0,
        NetPosition: item["Net Position"] || 0,
        Regime: "",
        CustomerType: "",
      }));

      // Merge API data with existing manual rows
      setRows((prevRows) => [...prevRows, ...apiRows]);
      setShowActionsHeader(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data from server.", {
        position: "top-right",
      });
    }
  };

  const handleAddOrUpdate = () => {
    setImResult([]);

    // Validate only when doing single-row update/add (optional for bulk)
    const isBulk = Array.isArray(editIndex);
    if (!isBulk) {
      const errs = validateRow(formData);
      if (errs.length > 0) {
        toast.error(errs.join("\n"), {
          position: "top-right",
          autoClose: 2000,
          theme: "colored",
        });
        return;
      }
    }

    if (editIndex !== null && !Array.isArray(editIndex)) {
      // Single-edit path (existing behavior)
      const updated = [...rows];
      updated[editIndex] = formData;
      setRows(updated);
      setEditIndex(null);
      toast.success("Edited successfully!");
    } else if (Array.isArray(editIndex)) {
      // Bulk-edit path: apply only fields that user entered in formData
      // We treat formData as Partial<FormData> — only non-empty keys will be applied.
      applyBulkUpdate(formData as Partial<FormData>);
    } else {
      // Add new row
      const errs = validateRow(formData);
      if (errs.length > 0) {
        toast.error(errs.join("\n"), {
          position: "top-right",
          autoClose: 2000,
          theme: "colored",
        });
        return;
      }

      setRows((prev) => [...prev, formData]);
      setShowActionsHeader(true);
      toast.success("Added successfully!");
    }

    setFormData(makeEmptyRow());
    setHasFormChanges(false);
  };

  const handleEdit = (index: number) => {
    setFormData(rows[index]);
    setEditIndex(index);
    setHasFormChanges(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleBulkEdit = (selectedRows: MRT_Row<any>[]) => {
    if (!selectedRows || selectedRows.length === 0) return;

    // collect numeric indexes
    const indexes = selectedRows.map((r) => r.index);

    // Clear form so user can enter values that will be applied to all selected rows.
    // You could alternatively prefill with common values across selected rows, but
    // keeping it empty is simpler and less error-prone.
    setFormData(makeEmptyRow());

    // store indexes array to indicate bulk-edit mode
    setEditIndex(indexes);
    setHasFormChanges(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyBulkUpdate = (updatedFields: Partial<FormData>) => {
    if (!Array.isArray(editIndex) || editIndex.length === 0) return;

    const newRows = [...rows];

    // For each selected row index, copy and merge only non-empty fields from updatedFields
    editIndex.forEach((idx) => {
      const original = newRows[idx] || {};
      const merged = { ...original };

      Object.entries(updatedFields).forEach(([key, value]) => {
        // apply field if user provided non-empty value (you can tweak logic)
        // treat empty string as "no change"
        if (value !== "" && value !== undefined && value !== null) {
          merged[key] = value;
        }
      });

      newRows[idx] = merged;
    });

    setRows(newRows);
    setFormData(makeEmptyRow());
    setEditIndex(null);
    setRowSelection({});
    toast.success("Bulk edit applied to selected rows!");
  };

  const handleDelete = (index: number) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };
  const handleBulkDelete = (selectedRows: MRT_Row<any>[]) => {
    if (selectedRows.length === 0) return;
    setBulkDeleteRows(selectedRows);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      const updated = [...rows];
      updated.splice(deleteIndex, 1);
      setRows(updated);
      toast.success("Deleted successfully!");
      setDeleteIndex(null);
    } else if (bulkDeleteRows.length > 0) {
      const updated = rows.filter(
        (row, index) =>
          !bulkDeleteRows.some((selectedRow) => selectedRow.index === index)
      );
      setRows(updated);
      setRowSelection({});
      toast.success("Selected rows deleted successfully!");
      setBulkDeleteRows([]);
    }

    setConfirmOpen(false);
  };

  // ❌ Cancel delete
  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
    setBulkDeleteRows([]);
  };

  const handleDownload = () => {
    const csv =
      rows
        .map((r) => CSV_COLUMNS.map((col) => r[col] ?? "").join(","))
        .join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "im.csv");
  };

  const handleClearForm = () => {
    setFormData(makeEmptyRow());
    setEditIndex(null);
    setHasFormChanges(false);
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    setUploadedFileName(file.name);

    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.trim().split("\n");

      const parsedRows = lines.map((line) => {
        const vals = line.split(",");
        const obj: any = {};
        CSV_COLUMNS.forEach((col, i) => (obj[col] = (vals[i] || "").trim()));
        return obj;
      });

      const validRows: any[] = [];
      const errorMessages: string[] = [];

      parsedRows.forEach((row, index) => {
        const errors = validateRow(row);
        if (errors.length > 0) {
          errorMessages.push(`Row ${index + 1}: ${errors.join(" | ")}`);
        } else {
          validRows.push(row);
        }
      });

      if (errorMessages.length > 0) {
        setUploadErrors(errorMessages);
        setErrorDialogOpen(true);
      }

      if (validRows.length > 0) {
        setRows((prev) => [...prev, ...validRows]);
        setUploadedFiles((prev) => [...prev, file.name]);
        toast.success(`${validRows.length} valid row(s) added successfully!`);
      }
    };

    reader.readAsText(file);
  };

  const renderAutocomplete = (field: string) => {
    const options =
      field === "ExchangeCode"
        ? Object.entries(EXCHANGE_CODES).map(([value, label]) => ({
          label: `${value} - ${label}`,
          value,
        }))
        : field === "ContractType"
          ? Object.entries(CONTRACT_TYPES).map(([value, label]) => ({
            label: `${value} - ${label}`,
            value,
          }))
          : field === "Regime"
            ? REGIMES.map((v) => ({ label: v || "-", value: v }))
            : CUSTOMER_TYPES.map((v) => ({ label: v || "-", value: v }));

    const value = options.find((opt) => opt.value === formData[field]) || null;

    return (
      <Autocomplete
        size="small"
        options={options}
        value={value}
        onChange={(_, newVal) => handleChange(field, newVal?.value || "")}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 180,
              "& .MuiAutocomplete-option": {
                fontSize: "0.7rem",
                minHeight: 26,
                py: 0.3,
              },
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "4px",
            backgroundColor: "transparent",
            "& fieldset": { border: "2px solid #ccc" },
            "&:hover fieldset": { borderColor: "#009db5" },
            "&.Mui-focused fieldset": {
              borderColor: "#009db5",
              borderWidth: "1.5px",
            },
            fontSize: "0.7rem",
            minHeight: 28,
          },
          "& .MuiInputBase-input": { padding: "4px 6px" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={FIELD_LABELS[field] || field}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#222",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              },
            }}
          />
        )}
      />
    );
  };

  const headerMap: Record<string, string> = {
    PositionFlag: "Position/Trade Flag",
    Account: "Account",
    ExchangeCode: "Exchange Code",
    ContractCode: "Contract Code",
    ContractType: "Contract Type",
    ExpiryYYYYMMDD: "Expiry (YYYYMMDD)",
    Strike: "Strike",
    NetPosition: "Net Position",
    Regime: "Regime",
    CustomerType: "Customer Type",
  };
  const columns = useMemo(
    () =>
      rows.length > 0
        ? Object.keys(rows[0]).map((key) => ({
          accessorKey: key,
          header: headerMap[key] || key, // ✅ use headerMap for display
          Cell: ({ cell }: any) => (
            <span style={{ fontSize: "0.60rem" }}>{cell.getValue()}</span>
          ),
        }))
        : [],
    [rows]
  );

  return (
    <div className="page-body">
      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          backgroundColor: "#fff",
          width: "100%",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#333" }}>
          Initial Margin
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap", // wrap on smaller screens
            alignItems: "center",
            gap: 1,
            mt: 2,
            mb: 3,
          }}
        >
          {/* Upload Box */}
          <Box
            sx={{
              flex: "0 1 200px",
              height: 32,
              border: "1.4px dashed rgba(150,160,180,0.6)",
              borderRadius: "8px",
              background: "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              cursor: "pointer",
              transition: "all 0.25s ease-in-out",
              "&:hover": {
                borderColor: "#3b82f6",
                background: "linear-gradient(160deg, #dce9ff 0%, #f0f2f5 100%)",
              },
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (
                file &&
                (file.name.endsWith(".csv") || file.name.endsWith(".txt"))
              ) {
                handleUpload({ target: { files: [file] } } as any);
              } else {
                toast.error("Please drop a valid .csv or .txt file.");
              }
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
            <Typography
              variant="body2"
              sx={{ color: "#2d3b50", fontWeight: 600, fontSize: "0.70rem" }}
            >
              Upload CSV
            </Typography>
            <Button
              variant="contained"
              size="small"
              component="label"
              sx={{
                textTransform: "none",
                fontSize: "0.55rem",
                borderRadius: "5px",
                px: 1,
                py: 0.2,
                minHeight: 24,
                marginLeft: "20px",
                background: "#009db5",
                "&:hover": {
                  background: "#009db5",
                },
              }}
            >
              Browse
              <input
                hidden
                accept=".csv,.txt"
                type="file"
                onChange={handleUpload}
              />
            </Button>
          </Box>

          {/* ✅ Show uploaded file names only if at least one file succeeded */}
          {uploadedFiles.length > 0 && (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                maxHeight: 90,
                overflowY: "auto",
                p: 0.5,
                border: "1px solid rgba(150,160,180,0.3)",
                borderRadius: "6px",
                background: "#f9fafb",
              }}
            >
              {uploadedFiles.map((name, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    background: "#fff",
                    px: 1,
                    py: 0.2,
                    borderRadius: "6px",
                    border: "1px solid rgba(150,160,180,0.2)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.6rem",
                      color: "#4b5563",
                      fontStyle: "italic",
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setUploadedFiles((prev) =>
                        prev.filter((_, idx) => idx !== i)
                      )
                    }
                    sx={{
                      p: 0,
                      color: "#dc2626",
                      "&:hover": { color: "#b91c1c" },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {/* Filter Toggle Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterAltIcon fontSize="inherit" />}
            sx={{
              flex: "0 1 80px",
              minWidth: 80,
              height: 32,
              textTransform: "none",
              fontSize: "0.65rem",
              fontWeight: 600,
              borderRadius: "8px",
              px: 1,
              borderColor: "rgba(150,160,180,0.6)",
              color: "#2d3b50",
              background: "linear-gradient(160deg, #f9fafb 0%, #f1f3f6 100%)",
              "&:hover": {
                borderColor: "#3b82f6",
                color: "#3b82f6",
                background: "linear-gradient(160deg, #e8f1ff 0%, #f4f5f7 100%)",
              },
            }}
            onClick={() => setShowFilter(!showFilter)}
          >
            Filter
          </Button>

          {/* Filter Fields */}
          {showFilter &&
            filtersConfig.map((f) => (
              <TextField
                key={f.key}
                select
                size="small"
                label={f.label}
                value={filters[f.key as keyof typeof filters]}
                onChange={(e) =>
                  setFilters({ ...filters, [f.key]: e.target.value })
                }
                InputProps={{
                  endAdornment: filters[f.key as keyof typeof filters] ? (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={() => setFilters({ ...filters, [f.key]: "" })}
                        sx={{
                          p: 0.6,
                          "& .MuiSvgIcon-root": { fontSize: "0.8rem" },
                        }}
                      >
                        <ClearIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: "0 1 140px",
                  minWidth: 120,
                  maxWidth: 160,
                  height: 34,
                  "& .MuiInputBase-root": {
                    fontSize: "0.7rem",
                    height: 34,
                    background:
                      "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                    borderRadius: "8px",
                    overflow: "visible",
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    textOverflow: "unset",
                    fontSize: "0.7rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                    top: -2,
                    color: "#3b4a66",
                    marginLeft: -1,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(150,160,180,0.4)",
                  },
                }}
              >
                {f.options.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: "0.7rem" }}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          {/* Clear All Icon */}
          {showFilter && (
            <IconButton
              size="small"
              onClick={() =>
                setFilters({
                  exchange: "",
                  book: "",
                  strategy: "",
                  clearer: "",
                  companyType: "",
                })
              }
              sx={{
                flex: "0 0 32px",
                height: 32,
                minWidth: 32,
                color: "#585bcc",
                backgroundColor: "rgba(59,130,246,0.1)",
                "&:hover": {
                  backgroundColor: "rgba(59,130,246,0.2)",
                },
              }}
              title="Clear All Filters"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}

          {/* Fetch Data Button */}
          {showFilter && (
            <Button
              variant="contained"
              onClick={handleFilterApply}
              sx={{
                flexShrink: 0,
                minWidth: 100,
                height: 32,
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 600,
                borderRadius: "6px",
                px: 2.5, // horizontal padding
                py: 0.5,
                backgroundColor: "#009db5",
                boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#008699",
                  boxShadow: "0px 3px 6px rgba(0,0,0,0.15)",
                },
                "&:active": {
                  backgroundColor: "#007b8a",
                  boxShadow: "inset 0px 2px 4px rgba(0,0,0,0.2)",
                },
              }}
            >
              Fetch Data
            </Button>
          )}
        </Box>

        {/* --- FORM --- */}
        <Accordion
          defaultActiveKey="0"
          className="table-ad__addInputs"
          style={{ width: "100%" }}
        >
          <Accordion.Item eventKey="0">
            <Accordion.Header
              className="accordion_button"
              style={{
                fontSize: "0.7rem",
                backgroundColor: "#d1edf1",
                padding: "6px 12px",
                borderRadius: "4px",
              }}
            >
              Manual Add / Edit Position
            </Accordion.Header>

            <Accordion.Body>
              {Array.isArray(editIndex) && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    fontWeight: 700,
                    color: "#b33a00",
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "0.75rem",
                  }}
                >
                  Bulk Edit Mode: Changes will apply to {editIndex.length}{" "}
                  selected rows.
                </Typography>
              )}
              <Grid
                container
                spacing={1.5}
                rowSpacing={1.5}
                columnSpacing={1.5}
              >
                {Object.keys(makeEmptyRow()).map((field) => (
                  <Grid item xs={12} sm={4} md={2.5} lg={2} key={field}>
                    {field === "PositionFlag" ? (
                      <TextField
                        fullWidth
                        size="small"
                        label="Position/Trade Flag"
                        value="P"
                        disabled
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                          sx: {
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#222",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            "& fieldset": { border: "2px solid #ccc" },
                            fontSize: "0.7rem",
                            minHeight: "34px",
                          },
                          "& .MuiInputBase-input": { padding: "4px 6px" },
                        }}
                      />
                    ) : field === "ExpiryYYYYMMDD" ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expiry Date (YYYYMMDD)"
                          views={
                            formData.ContractType === "D"
                              ? ["year", "month", "day"]
                              : ["year", "month"]
                          }
                          format={
                            formData.ContractType === "D"
                              ? "YYYYMMDD"
                              : "YYYYMM"
                          }
                          value={
                            formData[field]
                              ? dayjs(
                                formData.ContractType === "D"
                                  ? formData[field].toString()
                                  : formData[field].toString().slice(0, 6), // keep only YYYYMM
                                "YYYYMM"
                              )
                              : null
                          }
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleChange(field, "");
                              return;
                            }

                            if (formData.ContractType === "D") {
                              // Daily → YYYYMMDD
                              handleChange(field, newValue.format("YYYYMMDD"));
                            } else {
                              // Monthly → YYYYMM00 (no shift)
                              const formatted = `${newValue.format(
                                "YYYYMM"
                              )}00`;
                              handleChange(field, formatted);
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              variant: "outlined",
                              InputLabelProps: {
                                shrink: true,
                                sx: {
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: "#222",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                },
                              },
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "4px",
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "2px solid #ccc" },
                                  "&:hover fieldset": {
                                    borderColor: "#009db5",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#009db5",
                                    borderWidth: "1.5px",
                                  },
                                  fontSize: "0.7rem",
                                  minHeight: "34px",
                                },
                                "& input": {
                                  fontFamily: "monospace",
                                  letterSpacing: "0.4px",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "8px 8px",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    ) : [
                      "ExchangeCode",
                      "ContractType",
                      "Regime",
                      "CustomerType",
                    ].includes(field) ? (
                      renderAutocomplete(field)
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label={FIELD_LABELS[field] || field}
                        value={formData[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                          sx: {
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#222",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            "& fieldset": { border: "2px solid #ccc" },
                            "&:hover fieldset": { borderColor: "#009db5" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#009db5",
                              borderWidth: "1.5px",
                            },
                            fontSize: "0.7rem",
                            minHeight: "34px",
                          },
                          "& .MuiInputBase-input": { padding: "4px 6px" },
                        }}
                      />
                    )}
                  </Grid>
                ))}

                {/* Save / Update Button */}
                <Grid
                  item
                  xs={6}
                  sm={3}
                  md={1}
                  lg={1}
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                  gap={1}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddOrUpdate}
                    sx={{
                      height: { xs: "36px", sm: "32px" },
                      flex: { xs: "1 1 auto", sm: "0 0 auto" },
                      minWidth: { xs: "100%", sm: "100px" },
                      maxWidth: { sm: "150px" },
                      backgroundColor: "#009db5",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      "&:hover": {
                        backgroundColor: "#008c9e",
                      },
                    }}
                  >
                    {editIndex !== null ? "Update" : "Add"}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleClearForm}
                    disabled={!hasFormChanges}
                    sx={{
                      height: { xs: "36px", sm: "32px" },
                      flex: { xs: "1 1 auto", sm: "0 0 auto" },
                      minWidth: { xs: "100%", sm: "100px" },
                      fontSize: "0.75rem",
                      textTransform: "none",
                      maxWidth: { sm: "150px" },
                      borderColor: "#f44336",
                      color: "white",
                      "&:hover": {
                        borderColor: "#d32f2f",
                        backgroundColor: "#d32f2f",
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        {/* --- IM RESULT --- */}
        {imResult && (
          <Box
            onClick={() => setShowImTable((prev) => !prev)}
            sx={{
              background:
                "linear-gradient(145deg, #eef3ff 0%, #dbe8ff 50%, #f7faff 100%)",
              borderRadius: 2.5,
              border: "1px solid rgba(180, 200, 255, 0.7)",
              boxShadow:
                "inset 0 0 10px rgba(255,255,255,0.3), 0 4px 10px rgba(0,0,0,0.08)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              p: 2,
              mb: 1.5,
              cursor: "pointer",
              transition: "all 0.25s ease-in-out",
              "&:hover": {
                background: "linear-gradient(145deg, #e6eeff 0%, #d0deff 100%)",
                transform: "translateY(-1px)",
                boxShadow:
                  "inset 0 0 10px rgba(255,255,255,0.4), 0 6px 14px rgba(0,0,0,0.12)",
              },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#0f172a",
                fontWeight: 600,
                fontSize: "0.8rem",
                letterSpacing: 0.3,
              }}
            >
              {/* Left Side */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        imResult["Total Margin"] < 0 ? "#dc2626" : "#16a34a",
                      boxShadow:
                        imResult["Total Margin"] < 0
                          ? "0 0 6px rgba(220,38,38,0.5)"
                          : "0 0 6px rgba(22,163,74,0.5)",
                    }}
                  />
                  <Box component="span">Initial Margin</Box>
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: imResult["Total Margin"] < 0 ? "#dc2626" : "#16a34a",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                  }}
                >
                  {Number(imResult["Total Margin"]).toLocaleString()}
                </Box>
              </Box>
              {/* Right Side */}
              <Typography
                component="span"
                sx={{
                  color: "#2563eb",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  background:
                    "linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  transition: "opacity 0.2s ease-in-out",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                {showImTable ? "Hide Details" : "View Details"}
              </Typography>
            </Typography>
          </Box>
        )}
        {showImTable && imResult?.data?.length > 0 && (
          <MaterialReactTable
            columns={[
              {
                accessorKey: "Account",
                header: "Account",
                Cell: ({ cell }) => (
                  <span style={{ fontWeight: 500 }}>{cell.getValue()}</span>
                ),
              },
              {
                accessorKey: "Combined Contract",
                header: "Combined Contract",
                Cell: ({ cell }) => (
                  <span style={{ color: "#334155" }}>{cell.getValue()}</span>
                ),
              },
              {
                accessorKey: "Initial Margin",
                header: "Initial Margin",
                Cell: ({ cell }) => (
                  <span
                    style={{
                      color: cell.getValue() < 0 ? "#dc2626" : "#16a34a",
                      fontWeight: 600,
                    }}
                  >
                    {Number(cell.getValue()).toLocaleString()}
                  </span>
                ),
              },
            ]}
            data={imResult.data}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableSorting={false}
            enablePagination={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableRowSelection={false}
            muiTableContainerProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                border: "1px solid rgba(180,200,255,0.7)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                background: "linear-gradient(145deg, #f9fbff 0%, #eef3ff 100%)",
                overflow: "hidden",
                backdropFilter: "blur(4px)",
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "#1e293b",
                borderBottom: "2px solid #cbd5e1",
                background: "linear-gradient(90deg, #e6eeff 0%, #f3f7ff 100%)",
                textAlign: "center",
                padding: "6px 10px",
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: "0.7rem",
                padding: "5px 10px",
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#fff",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                transition: "background 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#f1f5ff",
                },
              },
            }}
          />
        )}
        {/* --- TABLE --- */}
        <div style={{ marginTop: 0 }}>
          <div className="table-ad__wrapperCard">
            <MaterialReactTable
              columns={[
                ...columns,
                {
                  id: "actions",
                  header: showActionsHeader ? "Actions" : "",
                  size: 80, // ⬅️ sets approximate width in px
                  muiTableHeadCellProps: {
                    sx: {
                      width: 80, // smaller header width
                      maxWidth: 80,
                      minWidth: 80,
                      padding: "4px",
                      borderRight: "1px solid #ddd",
                    },
                  },
                  muiTableBodyCellProps: {
                    sx: {
                      width: 80, // smaller body cell width
                      maxWidth: 80,
                      minWidth: 80,
                      padding: "4px",
                      borderRight: "1px solid #ddd",
                    },
                  },
                  Header: () => (showActionsHeader ? "Actions" : null),
                  Cell: ({ row }) => (
                    <Box display="flex" justifyContent="center" gap={0.3}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row.index)}
                          sx={{
                            p: 0.3, // reduced padding
                            "& svg": { fontSize: "0.9rem" }, // smaller icon size
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.index)}
                          sx={{
                            p: 0.3,
                            "& svg": { fontSize: "0.9rem" },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ),
                },
              ]}
              data={rows}
              enableColumnActions={false}
              enableColumnFilters={false}
              enableSorting
              enablePagination
              enableRowSelection
              onRowSelectionChange={setRowSelection}
              enableGlobalFilter={false}
              state={{ rowSelection, globalFilter }}
              onGlobalFilterChange={setGlobalFilter}
              initialState={{
                pagination: {
                  pageIndex: 0, // Set the initial page index to 0
                  pageSize: 100, // Default rows per page
                },
              }}
              renderToolbarInternalActions={({ table }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MRT_ToggleGlobalFilterButton table={table} />
                  <MRT_ToggleFiltersButton table={table} />
                  <MRT_ToggleDensePaddingButton table={table} />
                  <TableColumnVisibilityMenu table={table} />
                  <MRT_ToggleFullScreenButton table={table} />
                </Box>
              )}
              renderTopToolbarCustomActions={({ table }) => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={{ py: 0.3, px: 1 }}
                >
                  {/* LEFT — action buttons */}
                  <Box display="flex" alignItems="center" gap={1}>
                    {table.getSelectedRowModel().rows.length > 0 && (
                      <Button
                        variant="text"
                        startIcon={<DeleteIcon />}
                        onClick={() =>
                          handleBulkDelete(table.getSelectedRowModel().rows)
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          pl: 0,

                          color: "red !important", // force red text
                          "& .MuiSvgIcon-root": {
                            color: "red !important", // force red icon
                          },

                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "darkred !important",
                            "& .MuiSvgIcon-root": {
                              color: "darkred !important",
                            },
                          },
                        }}
                      >
                        Delete Selected (
                        {table.getSelectedRowModel().rows.length})
                      </Button>
                    )}
                    {table.getSelectedRowModel().rows.length > 0 && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Edit Selected">
                          <IconButton
                            size="small"
                            color="primary" // default color
                            onClick={() =>
                              handleBulkEdit(table.getSelectedRowModel().rows)
                            }
                            sx={{ ml: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Default color text */}
                        <Button
                          variant="text"
                          onClick={() =>
                            handleBulkEdit(table.getSelectedRowModel().rows)
                          }
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "inherit", // ← keeps default color
                            padding: 0,
                            minWidth: "unset",
                          }}
                        >
                          Edit Selected (
                          {table.getSelectedRowModel().rows.length})
                        </Button>
                      </Box>
                    )}

                    {rows.length > 0 && (
                      <Tooltip title="Validate All Rows">
                        <IconButton size="small" onClick={handleValidateAll}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Show icon + text when rows are selected */}
                    {table.getSelectedRowModel().rows.length > 0 && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Calculate IM for Selected">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleCalculateIM}
                              disabled={isCalculating}
                              sx={{ ml: 0.5 }}
                            >
                              {isCalculating ? (
                                <CircularProgress size={18} thickness={5} />
                              ) : (
                                <CalculateIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Button
                          variant="text"
                          onClick={handleCalculateIM}
                          disabled={isCalculating}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "inherit",
                            padding: 0,
                            minWidth: "unset",
                          }}
                        >
                          Calculate Selected ({table.getSelectedRowModel().rows.length})
                        </Button>
                      </Box>
                    )}

                    {/* Show only icon when NO rows selected */}
                    {table.getSelectedRowModel().rows.length === 0 && (
                      <Tooltip title="Calculate IM">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleCalculateIM}
                            disabled={rows.length === 0 || isCalculating}
                          >
                            {isCalculating ? (
                              <CircularProgress size={18} thickness={5} />
                            ) : (
                              <CalculateIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Button
                      variant="text"
                      onClick={() => {
                        setRows([]);
                        setImResult(null);
                        setShowActionsHeader(false);
                      }}
                      startIcon={<RefreshIcon />}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: "#0277bd",
                        px: 2,
                        "&:hover": { backgroundColor: "rgba(2,119,189,0.1)" },
                      }}
                    >
                      Clear
                    </Button>
                  </Box>

                  {/* RIGHT — Search + Download */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      variant="outlined"
                      value={globalFilter || ""}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 32,
                          fontSize: "0.75rem",
                          background: "#f9fafb",
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "4px 8px",
                        },
                        width: 160,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon sx={{ fontSize: 16, color: "#888" }} />
                          </InputAdornment>
                        ),
                        "aria-label": "table search",
                      }}
                    />

                    {rows.length > 0 && (
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={handleDownload}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
              muiTopToolbarProps={{
                sx: {
                  backgroundColor: "#fff",
                  padding: "8px",
                  // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
                  "& .MuiBox-root.css-wsew38": {
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  },
                },
                className: "top-toolbar-wrapper__new",
              }}
              muiBottomToolbarProps={{
                sx: {
                  backgroundColor: "#fff",
                  padding: "8px",
                },
                className: "bottom-toolbar-wrapper__new",
              }}
              muiTableHeadCellProps={{
                sx: {
                  border: "1px solid #e0e0e087",
                  fontStyle: "normal",
                  fontWeight: "normal",
                  "& .MuiSvgIcon-root": { fontSize: 18 },
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  fontSize: "0.75rem",
                  padding: "4px 6px", // compact padding
                  lineHeight: 1, // reduce row height
                  minHeight: 28, // fix row height
                  backgroundColor: "transparent !important",
                  "& .MuiSvgIcon-root": { fontSize: 18 },
                  "& .MuiCheckbox-root": {
                    paddingLeft: "15px !important",
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  backgroundColor: "#fff",
                },
                className: "table-container-wrapper__new",
              }}
            />
          </div>
        </div>
        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">
          Notes: Strike must be numeric. Use DD=00 for monthly futures. Regime
          and Customer Type optional.
        </Typography>
      </Paper>
      <Dialog
        open={confirmOpen}
        onClose={cancelDelete}
        PaperProps={{
          sx: {
            width: 320,
            p: 1,
            borderRadius: 2,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            px: 2,
            pt: 2.5,
            pb: 1,
          }}
        >
          <Box display="flex" justifyContent="center" mb={1}>
            <WarningAmberRoundedIcon sx={{ color: "#f59e0b", fontSize: 34 }} />
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 0.5, fontSize: "0.9rem" }}
          >
            Confirm Delete
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            {deleteIndex !== null
              ? "Delete this record?"
              : "Delete selected records?"}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Button
            onClick={cancelDelete}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#b71c1c",
            bgcolor: "#ffebee",
            borderBottom: "1px solid #f5c2c2",
          }}
        >
          Validation Errors
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            p: 1.5,
            bgcolor: "#fffdfd",
          }}
        >
          {uploadErrors.map((err, i) => (
            <Box
              key={i}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: "6px",
                bgcolor: "#fff3f3",
                border: "1px solid #ffcdd2",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#b71c1c", fontSize: "0.8rem", lineHeight: 1.3 }}
              >
                {err}
              </Typography>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ p: 1 }}>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              bgcolor: "#b71c1c",
              "&:hover": { bgcolor: "#a31212" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InitialMargin;
